import {
  MediaImageStudio,
  MediaImageStudioEvents,
  MediaImageStudioMode,
  SaveStrategy,
} from '@wix/media-image-studio-opener/dist/src';
import { has, isEmpty, join } from 'ramda';
import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { BiAddMediaActionTarget } from '../../../constants/Bi';
import { ItemType } from '../../../constants/ItemType';
import { MainSource } from '../../../constants/Source';
import { SystemFolderId } from '../../../constants/SystemFolderId';
import { extractCommonBiFieldsFromItems } from '../../bi';
import { getLocale } from '../../env/getLocale';
import { startInteractionLogging } from '../../fedops/imageLoadedAfterMediaStudioSave';
import { useMMGRSettings } from '../../hoc/withMMGRSettings';
import { MediaManagerSDKFile } from '../../media-manager-sdk';
import { isBoard } from '../../media-manager-sdk/boards';
import { isUnsplashItem } from '../../media-manager-sdk/items';
import { parseSiteMediaToken } from '../../siteMediaToken';
import { useFileType } from '../bi/useFileType';
import { useTrackAddMediaUploadProcessAnalytics } from '../bi/useTrackAddMediaUploadProcessAnalytics';
import { useAppStatus } from '../useAppStatus';
import { IUseMediaStudioContext, TOpenAIImageCreation, TOpenMediaImageStudio } from './useMediaStudio.types';
import { useStorageQuotaAwareHandler } from '../storageQuota/useStorageQuotaAwareHandler';
import { StorageQuotaUseCase } from '../storageQuota/useStorageQuotaAwareHandler/StorageQuotaUseCase';

export const MediaStudioContext = createContext<IUseMediaStudioContext>({
  mediaStudio: null,
  openMediaStudio: () => {},
  openAIImageCreation: () => {},
});

export const MediaStudioProvider: FC = ({ children }) => {
  const { mmgrSettings } = useMMGRSettings();
  const { isAppWarmingUp } = useAppStatus();
  const { siteMediaToken, publicMediaRoot } = mmgrSettings;
  const preloadTimeoutId = useRef<number>(null);

  const joinedPublicMediaRoot = join(',', publicMediaRoot);

  const mediaStudio = useMemo(() => {
    const { metaSiteId } = parseSiteMediaToken(siteMediaToken);

    return new MediaImageStudio({
      initiator: 'media-manager-g6',
      mediaRoot: joinedPublicMediaRoot,
      siteToken: siteMediaToken,
      metasiteId: metaSiteId,
      locale: getLocale(),
      saveStrategy: SaveStrategy.Upload,
    });
  }, [siteMediaToken, joinedPublicMediaRoot]);

  const openMediaStudio: TOpenMediaImageStudio = useCallback(
    ({ file = null, mode = MediaImageStudioMode.Transform, targetFolder = null } = {}) => {
      const openOptions = file
        ? {
            ...(isUnsplashItem(file) ? { fileUrl: file.fileUrl } : { fileId: file.id }),
            targetFolder: targetFolder && (isBoard(targetFolder) ? SystemFolderId.SiteFiles : targetFolder.id),
          }
        : { isUpload: true, mode: MediaImageStudioMode.Transform };
      mediaStudio.show({ mode, ...openOptions });
    },
    [mediaStudio],
  );

  const openAIImageCreation: TOpenAIImageCreation = useCallback(
    ({ targetFolder }) => {
      mediaStudio.show({
        targetFolder: targetFolder && (isBoard(targetFolder) ? SystemFolderId.SiteFiles : targetFolder.id),
        mode: MediaImageStudioMode.Generate,
      });
    },
    [mediaStudio],
  );

  const { trackAddMediaFinished } = useTrackAddMediaUploadProcessAnalytics();
  const fileType = useFileType();

  useEffect(() => {
    if (!mediaStudio) {
      return;
    }

    const uploadedFileSubscription = mediaStudio.on(MediaImageStudioEvents.UploadedFile, (data) => {
      const item = data as unknown as MediaManagerSDKFile;

      startInteractionLogging(item.id);

      trackAddMediaFinished({
        success: true,
        batch: false,
        batchSize: 1,
        target: BiAddMediaActionTarget.CropEdit,
        correlationId: null,
        isTags: has('tags', item) && !isEmpty(item.tags),
        ...extractCommonBiFieldsFromItems([item], { fileType }),
        loadingTime: 0,
      });
    });

    return () => uploadedFileSubscription.remove();
  }, [mediaStudio, trackAddMediaFinished, fileType]);

  useEffect(() => {
    if (!mediaStudio || isAppWarmingUp || preloadTimeoutId.current) {
      return;
    }

    if (process.env.NODE_ENV === 'production') {
      preloadTimeoutId.current = window.setTimeout(() => {
        mediaStudio.preload();
      }, 5000);
    }
  }, [mediaStudio, isAppWarmingUp]);

  useEffect(() => {
    return () => {
      clearTimeout(preloadTimeoutId.current);

      preloadTimeoutId.current = null;
      mediaStudio.kill();
    };
  }, [mediaStudio]);

  const storageQuotaAware = useStorageQuotaAwareHandler();

  return (
    <MediaStudioContext.Provider
      value={{
        mediaStudio,
        openMediaStudio: storageQuotaAware(openMediaStudio, StorageQuotaUseCase.MEDIA_STUDIO),
        openAIImageCreation: storageQuotaAware(openAIImageCreation, StorageQuotaUseCase.MEDIA_STUDIO),
      }}
    >
      {children}
    </MediaStudioContext.Provider>
  );
};

export const useMediaStudio = () => useContext(MediaStudioContext);

export const useMediaStudioOnFileUploaded = (onFileUploaded: (file: MediaManagerSDKFile) => void) => {
  const { mediaStudio } = useMediaStudio();

  useEffect(() => {
    if (!mediaStudio) {
      return;
    }

    const uploadedFileSubscription = mediaStudio.on(MediaImageStudioEvents.UploadedFile, (data) => {
      onFileUploaded({ ...data, kind: ItemType.File, source: MainSource.SiteFiles } as MediaManagerSDKFile);
    });

    return () => uploadedFileSubscription.remove();
  }, [mediaStudio, onFileUploaded]);
};
